import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { useMinistry } from '@/contexts/MinistryContext';
import { toast } from 'sonner';
import { Calendar, MapPin, Plus, Trash2, Eye, Search, Filter, CheckCircle, Clock, XCircle, TrendingUp, Users, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const Events = () => {
  const { currentMinistry, loading: ministryLoading } = useMinistry();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventStats, setEventStats] = useState(null);
  const [globalStats, setGlobalStats] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  
  const [newEvent, setNewEvent] = useState({
    name: '',
    date: '',
    location: '',
    status: 'agendado',
    type: 'culto'
  });

  useEffect(() => {
    if (!ministryLoading && currentMinistry?.id) {
      fetchEvents();
      fetchGlobalStats();
    } else if (!ministryLoading && !currentMinistry) {
      setLoading(false);
    }
  }, [currentMinistry?.id, ministryLoading]);

  const fetchEvents = async () => {
    if (!currentMinistry?.id) return;
    try {
      const response = await axios.get(`${API}/events?ministry_id=${currentMinistry.id}`);
      setEvents(response.data);
    } catch (error) {
      toast.error('Erro ao carregar eventos');
    } finally {
      setLoading(false);
    }
  };

  const fetchGlobalStats = async () => {
    if (!currentMinistry?.id) return;
    try {
      const response = await axios.get(`${API}/events/stats?ministry_id=${currentMinistry.id}`);
      setGlobalStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/events?ministry_id=${currentMinistry?.id}`, newEvent);
      toast.success('Evento criado com sucesso!');
      setDialogOpen(false);
      setNewEvent({ name: '', date: '', location: '', status: 'agendado', type: 'culto' });
      fetchEvents();
    } catch (error) {
      toast.error('Erro ao criar evento');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Tem certeza que deseja excluir este evento? Os check-ins associados também serão afetados.')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/events/${eventId}`);
      toast.success('Evento excluído!');
      fetchEvents();
    } catch (error) {
      toast.error('Erro ao excluir evento');
    }
  };

  const handleViewEvent = async (event) => {
    setSelectedEvent(event);
    setViewDialogOpen(true);
    
    // Busca estatísticas de check-in do evento
    try {
      const response = await axios.get(`${API}/checkins/event/${event.id}`);
      setEventStats(response.data);
    } catch (error) {
      setEventStats(null);
    }
  };

  const handleOpenEditDialog = (event) => {
    setEditingEvent({
      id: event.id,
      name: event.name,
      date: event.date ? event.date.slice(0, 16) : '', // formato datetime-local
      location: event.location || '',
      status: event.status,
      type: event.type
    });
    setEditDialogOpen(true);
  };

  const handleUpdateEvent = async (e) => {
    e.preventDefault();
    if (!editingEvent) return;
    
    try {
      await axios.put(`${API}/events/${editingEvent.id}`, {
        name: editingEvent.name,
        date: editingEvent.date,
        location: editingEvent.location,
        status: editingEvent.status,
        type: editingEvent.type
      });
      toast.success('Evento atualizado com sucesso!');
      setEditDialogOpen(false);
      setEditingEvent(null);
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar evento');
    }
  };

  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('pt-BR', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const formatDateShort = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('pt-BR', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'finalizado': { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Finalizado' },
      'em_andamento': { color: 'bg-orange-100 text-orange-800', icon: Clock, label: 'Em Andamento' },
      'agendado': { color: 'bg-blue-100 text-blue-800', icon: Clock, label: 'Agendado' },
      'cancelado': { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Cancelado' }
    };
    
    const config = statusConfig[status] || statusConfig['agendado'];
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon size={12} className="mr-1" />
        {config.label}
      </span>
    );
  };

  const filteredEvents = events.filter(event => {
    const matchesSearch = event.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (event.location && event.location.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = statusFilter === 'all' || event.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Agrupa eventos por mês
  const groupedEvents = filteredEvents.reduce((groups, event) => {
    const date = new Date(event.date);
    const monthYear = date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
    if (!groups[monthYear]) {
      groups[monthYear] = [];
    }
    groups[monthYear].push(event);
    return groups;
  }, {});

  // Estatísticas
  const stats = {
    total: events.length,
    finalizados: events.filter(e => e.status === 'finalizado').length,
    em_andamento: events.filter(e => e.status === 'em_andamento').length,
    agendados: events.filter(e => e.status === 'agendado').length,
    cancelados: events.filter(e => e.status === 'cancelado').length
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Eventos</h1>
          <p className="text-gray-500 mt-1">Gerencie os cultos e eventos do ministério</p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-primary flex items-center space-x-2">
              <Plus size={20} />
              <span>Novo Evento</span>
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Criar Novo Evento</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateEvent} className="space-y-4">
              <div>
                <Label>Nome do Evento *</Label>
                <Input
                  value={newEvent.name}
                  onChange={(e) => setNewEvent({...newEvent, name: e.target.value})}
                  placeholder="Ex: Pulse no Deck - Dezembro"
                  required
                />
              </div>
              <div>
                <Label>Data e Hora *</Label>
                <Input
                  type="datetime-local"
                  value={newEvent.date}
                  onChange={(e) => setNewEvent({...newEvent, date: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label>Local</Label>
                <Input
                  value={newEvent.location}
                  onChange={(e) => setNewEvent({...newEvent, location: e.target.value})}
                  placeholder="Ex: Red Hall"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Tipo</Label>
                  <Select value={newEvent.type} onValueChange={(value) => setNewEvent({...newEvent, type: value})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="culto">Culto</SelectItem>
                      <SelectItem value="evento">Evento Especial</SelectItem>
                      <SelectItem value="retiro">Retiro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={newEvent.status} onValueChange={(value) => setNewEvent({...newEvent, status: value})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="agendado">Agendado</SelectItem>
                      <SelectItem value="em_andamento">Em Andamento</SelectItem>
                      <SelectItem value="finalizado">Finalizado</SelectItem>
                      <SelectItem value="cancelado">Cancelado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                <Button type="submit" className="btn-primary">Criar Evento</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <div className="card bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-600 font-medium">Total de Eventos</p>
              <p className="text-3xl font-bold text-blue-800">{stats.total}</p>
            </div>
            <Calendar className="text-blue-400" size={32} />
          </div>
        </div>
        <div className="card bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-purple-600 font-medium">Média de Presença</p>
              <p className="text-3xl font-bold text-purple-800">{globalStats?.average_attendance || 0}</p>
            </div>
            <TrendingUp className="text-purple-400" size={32} />
          </div>
        </div>
        <div className="card bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-600 font-medium">Finalizados</p>
              <p className="text-3xl font-bold text-green-800">{stats.finalizados}</p>
            </div>
            <CheckCircle className="text-green-400" size={32} />
          </div>
        </div>
        <div className="card bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-orange-600 font-medium">Em Andamento</p>
              <p className="text-3xl font-bold text-orange-800">{stats.em_andamento}</p>
            </div>
            <Clock className="text-orange-400" size={32} />
          </div>
        </div>
        <div className="card bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-amber-600 font-medium">Agendados</p>
              <p className="text-3xl font-bold text-amber-800">{stats.agendados}</p>
            </div>
            <Clock className="text-amber-400" size={32} />
          </div>
        </div>
        <div className="card bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-red-600 font-medium">Cancelados</p>
              <p className="text-3xl font-bold text-red-800">{stats.cancelados}</p>
            </div>
            <XCircle className="text-red-400" size={32} />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <Input
              placeholder="Buscar por nome ou local..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="w-full md:w-48">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <Filter size={16} className="mr-2" />
                <SelectValue placeholder="Filtrar por status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os status</SelectItem>
                <SelectItem value="em_andamento">Em Andamento</SelectItem>
                <SelectItem value="agendado">Agendados</SelectItem>
                <SelectItem value="finalizado">Finalizados</SelectItem>
                <SelectItem value="cancelado">Cancelados</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Events List */}
      {loading ? (
        <div className="text-center py-12">Carregando eventos...</div>
      ) : filteredEvents.length === 0 ? (
        <div className="card text-center py-12">
          <Calendar size={64} className="mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500 text-lg">Nenhum evento encontrado</p>
          <p className="text-gray-400 text-sm mt-2">Crie um novo evento ou ajuste os filtros</p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedEvents).map(([monthYear, monthEvents]) => (
            <div key={monthYear}>
              <h3 className="text-lg font-semibold text-gray-700 mb-3 capitalize">{monthYear}</h3>
              <div className="space-y-3">
                {monthEvents.map((event) => (
                  <div 
                    key={event.id} 
                    className="card hover:shadow-lg transition flex items-center justify-between"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex flex-col items-center justify-center text-white">
                        <span className="text-xl font-bold">{new Date(event.date).getDate()}</span>
                        <span className="text-xs uppercase">{new Date(event.date).toLocaleDateString('pt-BR', { month: 'short' })}</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">{event.name}</h4>
                        <div className="flex items-center space-x-3 text-sm text-gray-500 mt-1">
                          {event.location && (
                            <span className="flex items-center">
                              <MapPin size={14} className="mr-1" />
                              {event.location}
                            </span>
                          )}
                          <span className="flex items-center">
                            <Clock size={14} className="mr-1" />
                            {formatDate(event.date)}
                          </span>
                        </div>
                        {/* Estatísticas de presença */}
                        <div className="flex items-center space-x-3 mt-2">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            <Users size={12} className="mr-1" />
                            {event.total_presentes || 0} presentes
                          </span>
                          {(event.visitantes_primeira_vez || 0) > 0 && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              {event.visitantes_primeira_vez} visitantes 1ª vez
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      {getStatusBadge(event.status)}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleViewEvent(event)}
                        className="text-blue-600 hover:bg-blue-50"
                        title="Visualizar"
                      >
                        <Eye size={18} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenEditDialog(event)}
                        className="text-amber-600 hover:bg-amber-50"
                        title="Editar"
                      >
                        <Pencil size={18} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteEvent(event.id)}
                        className="text-red-600 hover:bg-red-50"
                        title="Excluir"
                      >
                        <Trash2 size={18} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* View Event Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedEvent?.name}</DialogTitle>
          </DialogHeader>
          
          {selectedEvent && (
            <div className="space-y-6">
              {/* Event Details */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">Data e Hora</p>
                  <p className="font-semibold">{formatDate(selectedEvent.date)}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">Local</p>
                  <p className="font-semibold">{selectedEvent.location || 'Não informado'}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">Tipo</p>
                  <p className="font-semibold capitalize">{selectedEvent.type}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">Status</p>
                  {getStatusBadge(selectedEvent.status)}
                </div>
              </div>

              {/* Check-in Stats */}
              {eventStats && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Estatísticas de Check-in</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-4 bg-blue-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-blue-600">{eventStats.total}</p>
                      <p className="text-sm text-blue-800">Total de Check-ins</p>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-green-600">{eventStats.members}</p>
                      <p className="text-sm text-green-800">Membros</p>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-purple-600">{eventStats.visitors + eventStats.first_time_visitors}</p>
                      <p className="text-sm text-purple-800">Visitantes</p>
                    </div>
                  </div>
                </div>
              )}

              {selectedEvent.row_id && (
                <div className="text-xs text-gray-400">
                  ID Original: {selectedEvent.row_id}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Event Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Evento</DialogTitle>
          </DialogHeader>
          {editingEvent && (
            <form onSubmit={handleUpdateEvent} className="space-y-4">
              <div>
                <Label>Nome do Evento *</Label>
                <Input
                  value={editingEvent.name}
                  onChange={(e) => setEditingEvent({...editingEvent, name: e.target.value})}
                  placeholder="Ex: Pulse no Deck - Dezembro"
                  required
                />
              </div>
              <div>
                <Label>Data e Hora *</Label>
                <Input
                  type="datetime-local"
                  value={editingEvent.date}
                  onChange={(e) => setEditingEvent({...editingEvent, date: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label>Local</Label>
                <Input
                  value={editingEvent.location}
                  onChange={(e) => setEditingEvent({...editingEvent, location: e.target.value})}
                  placeholder="Ex: Red Hall"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Tipo</Label>
                  <Select value={editingEvent.type} onValueChange={(value) => setEditingEvent({...editingEvent, type: value})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="culto">Culto</SelectItem>
                      <SelectItem value="evento">Evento Especial</SelectItem>
                      <SelectItem value="retiro">Retiro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={editingEvent.status} onValueChange={(value) => setEditingEvent({...editingEvent, status: value})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="agendado">Agendado</SelectItem>
                      <SelectItem value="em_andamento">Em Andamento</SelectItem>
                      <SelectItem value="finalizado">Finalizado</SelectItem>
                      <SelectItem value="cancelado">Cancelado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>Cancelar</Button>
                <Button type="submit" className="btn-primary">Salvar Alterações</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Events;
